import React, { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IconClose } from '../../app/icons/close';
import { Loading } from '../../app/loading/loading';
import { ModalAlerts } from '../alerts/modal/alerts';
import { cn } from '../../../utils/tailwind';

type ModalType = {
    children: React.ReactNode;
    show?: boolean;
    title?: React.ReactNode | string;
    ariaLabel?: string;
    icon?: React.ReactNode;
    footer?: React.ReactNode;
    onClose?: () => void;
    loading?: boolean;
    width?: number;
    showCloseButton?: boolean;
    account?: boolean;
    titleAccessory?: React.ReactNode;
    backdropContent?: React.ReactNode;
};

export const Modal: React.FC<ModalType> = ({
    show,
    title,
    ariaLabel,
    icon,
    children,
    onClose,
    footer,
    loading = false,
    width,
    showCloseButton = true,
    account = false,
    titleAccessory,
    backdropContent,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!show) return undefined;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const focusTarget =
            contentRef.current?.querySelector<HTMLElement>('input:not([disabled])') ??
            contentRef.current?.querySelector<HTMLElement>('button, a[href]');
        focusTarget?.focus();
        return () => previouslyFocused?.focus();
    }, [show]);

    useEffect(() => {
        if (!show) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [show]);

    useEffect(() => {
        if (!show || !account) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showCloseButton) onClose?.();
            if (event.key !== 'Tab') return;
            const targets = Array.from(
                contentRef.current?.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]',
                ) ?? [],
            ).filter((element) => element.getClientRects().length > 0);
            const first = targets[0];
            const last = targets[targets.length - 1];
            if (!contentRef.current?.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first)?.focus();
            } else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [show, account, onClose, showCloseButton]);

    return show ? (
        <div className="fixed inset-0 z-[1000] p-[1rem] backdrop-blur-[0.5rem] md:flex md:items-center md:justify-center md:p-[2rem]">
            <div
                ref={contentRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
                className={cn(
                    'relative z-[1200] max-h-full w-full overflow-auto',
                    account
                        ? 'rounded-[2.4rem] p-[2rem] sm:p-[3.2rem]'
                        : 'rounded-medium p-[2rem] md:px-[6.4rem] md:pb-[6.4rem] md:pt-[3.2rem]',
                )}
                style={{
                    backgroundColor: account ? '#fdf7f2' : '#ffffff',
                    backgroundImage: account
                        ? 'linear-gradient(to bottom, #fffaf7 0, #fdf7f2 12rem, #fdf7f2 calc(100% - 10rem), #f2e9e1 100%)'
                        : undefined,
                    maxWidth: `${width ?? (account ? 56 : 62)}rem`,
                }}
            >
                <Loading active={loading}>
                    {account ? (
                        <div className="mb-[3.2rem] flex flex-wrap items-center gap-x-[1.6rem]">
                            <h2
                                className={cn(
                                    'text-[2.4rem] font-bold leading-[1.25] text-brand-secondary',
                                    titleAccessory
                                        ? 'mb-[1.2rem] basis-full pr-[3.2rem] md:mb-0 md:basis-auto md:pr-0'
                                        : 'pr-[3.2rem]',
                                )}
                            >
                                {title}
                            </h2>
                            {titleAccessory}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center pt-medium">
                            {icon}
                            <div className="mb-medium mt-small text-center text-xlarge leading-xlarge font-bold text-brand-secondary">
                                {title}
                            </div>
                        </div>
                    )}
                    <ModalAlerts />
                    {children}
                    {footer && (
                        <div className="mt-medium flex items-center justify-between [&>*:only-child]:ml-auto">
                            {footer}
                        </div>
                    )}
                </Loading>
                {showCloseButton && (
                    <button
                        type="button"
                        aria-label={t('dialogs.common.close')}
                        className={cn(
                            'absolute flex items-center justify-center cursor-pointer border-0 p-0 [&_i]:text-[var(--modal-close-color)] hover:[&_i]:text-[var(--modal-close-hover-color)]',
                            'transition-[background-color,color,opacity] duration-200 ease-out motion-reduce:transition-none [&_i]:transition-colors [&_i]:duration-200 [&_i]:ease-out motion-reduce:[&_i]:transition-none',
                            account
                                ? 'right-[0.8rem] top-[1.3rem] h-[4.4rem] w-[4.4rem] rounded-full bg-[#d9cdc5]/20 [&_i]:h-[1.8rem] [&_i]:w-[1.8rem] hover:bg-[#d9cdc5]/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-primary sm:right-[2rem]'
                                : 'right-[1.5rem] top-[1.5rem] bg-transparent [&_i]:h-[3rem] [&_i]:w-[3rem]',
                            account && (titleAccessory ? 'sm:top-[2.5rem] md:top-[3.2rem]' : 'sm:top-[2.5rem]'),
                        )}
                        onClick={onClose}
                        style={{
                            ['--modal-close-color' as string]: account ? '#9b8d82' : theme.modal.close.base.color,
                            ['--modal-close-hover-color' as string]: account
                                ? '#72645a'
                                : theme.modal.close.hover.color,
                        }}
                    >
                        <IconClose />
                    </button>
                )}
            </div>

            {backdropContent}

            {showCloseButton ? (
                <div
                    className="fixed inset-0 z-[1100]"
                    onClick={onClose}
                    style={{ backgroundColor: 'rgb(114 100 90 / 45%)' }}
                />
            ) : (
                <div className="fixed inset-0 z-[1100]" style={{ backgroundColor: 'rgb(114 100 90 / 45%)' }} />
            )}
        </div>
    ) : null;
};
